import { React, useApi, useParams, useNavigate, useState, DataGrid, GridColDef, GridRowSelectionModel, Dialog, DialogActions, CloseIcon, DialogContent, DialogTitle, Divider, IconButton, Box, useEffect, Typography, SearchBar, useMemo } from '../../../utils/globalImports';
import { CustomButton, Toaster, FeeStageType } from '../../../utils/globalImports';
import { ApiResponse } from '../../../services/apiService';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: () => void;  // Function to handle modal close
    frameworkId: number;
    afterSubmit: () => void;  // Function to handle modal close
}

interface FeeStage {
    feeStageId: number;
    frameworkId: number;
    refrenceNumber: string;
    description: string;
    value: number;
    valuePer: string;
    completionNotes: string;
    status: boolean;
}

interface ProposalFeeStage {
    proposalId: number;
    projectId: number;
    frameworkId: number;
    feeStageId: number;
    type: number;
    refrenceNumber: string;
    description: string;
    unitPrice: number;
    unitPricePer: string;
    status?: number;
    completionNotes: string;
    createdId: number;
}

const ProposalAddFeeStageModal: React.FC<ModalProps> = ({ open, onClose, frameworkId, afterSubmit }) => {

    const { execute } = useApi();
    const navigate = useNavigate();
    const [feeStageOption, setFeeStageOption] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });

    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL

    const userId = Number(localStorage.getItem("userId"));
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    const [searchQuery, setSearchQuery] = useState("");

    const filteredFeeStages = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return feeStageOption;
    return feeStageOption.filter((row) =>
        row.refrenceNumber?.toLowerCase().includes(q) ||
        row.Description?.toLowerCase().includes(q)
    );
}, [searchQuery, feeStageOption]);

    useEffect(() => {
        fetchfeeStages()
    }, [frameworkId])

    useEffect(() => {
        if (!open) setSearchQuery("");
        setSelectedIds([]);
    }, [open]);

    // fetch feeStages
    const fetchfeeStages = async () => {
        const { data } = await execute<FeeStage[]>('get', `/Framework/GetFeeStagesByFrameworkId/${frameworkId}`);
        if (data) {
            const activeFeeStage = data
                .filter((feeStage: any) => feeStage.status)
                .map((feeStage) => {

                    const { feeStageId, refrenceNumber, description, value, valuePer, completionNotes } = feeStage;

                    return {
                        feeStageId,
                        refrenceNumber,
                        value,
                        valuePer,
                        "Description": description,
                        "Value": value + ' ' + valuePer,
                        "Terms and Notes": completionNotes
                    }
                })

            setFeeStageOption(activeFeeStage)
        }
    };

    const columns: GridColDef<(typeof feeStageOption)[number]>[] = [

        {
            field: 'refrenceNumber', headerName: 'Reference No', flex: 1,
            align: 'center',
        },
        {
            field: 'Description', headerName: 'Description', flex: 1,
            align: 'center',
        },
        {
            field: 'Value',
            headerName: 'Value',
            flex: 1,
            align: 'center',
        },
        {
            field: 'Terms and Notes',
            headerName: 'Terms and Notes',
            flex: 1,
            align: 'center',
        },
    ];

    const handleNavigate = async () => {
        await delay(2000); // Apply delay for 2 seconds if shouldDelay is true
        afterSubmit();
    };


    const handleSubmit = async () => {

        let response: ApiResponse<any>;
        const selectedRows = feeStageOption.filter((row) => selectedIds.includes(row.feeStageId));

        if (selectedRows.length > 0) {
            // Map selected rows to ProposalFeeStage array
            const proposalFeeStages: ProposalFeeStage[] = selectedRows.map((row) => ({
                proposalId: Number(proposalId),
                projectId: Number(projectId),
                frameworkId: Number(frameworkId),
                feeStageId: row.feeStageId,
                type: FeeStageType.Framework,
                refrenceNumber: row.refrenceNumber,
                description: row.Description,
                unitPrice: row.value,
                unitPricePer: row.valuePer,
                completionNotes: row["Terms and Notes"],
                createdId: userId
            }));

            response = await execute('post', '/Proposal/framework/AddfeeStage', proposalFeeStages);

            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: response.error, type: "error" });
                } else {
                    setToast({ open: true, message: response.data, type: "success" });
                    handleNavigate();
                }
            }
        }
        else setToast({ open: true, message: "Please select applicable fee stages", type: "error" });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    position: 'relative',
                    color: '#1e66ab',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 0,
                    fontWeight: 500,
                    fontSize: '24px'
                }}>
                Select applicable fee stages

                <IconButton
                    color="inherit"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 24,
                        top: 16,
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '6px',
                        color: '#1e66ab',
                        '&:hover': {
                            backgroundColor: '#f8fafc'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider></Divider>

            <DialogContent>

                <Typography sx={{ color: '#1e66ab', fontSize: '14px', fontWeight: 500, mb: 2 }}>
                    Select one or more fee stages to add.
                </Typography>

                <Box sx={{
                    border: '1px solid #E0E0E0',
                    borderBottom: 'none',
                    borderRadius: '10px 10px 0 0',
                    overflow: 'hidden',
                }}>

                    {/* Search input */}
                    <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#fff', width: '50%' }}>
                        <SearchBar
                            placeholder="Search by reference no. or description..."
                            onQueryChange={setSearchQuery}
                            sx={{}}
                        />
                    </Box>
                </Box>

                <Box sx={{height: 400, width: '100%' }}>
                    <DataGrid
                        rows={filteredFeeStages}
                        columns={columns}
                        getRowId={(row) => row.feeStageId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 5,
                                },
                            },
                        }}
                        sx={{
                           
                            "& .MuiDataGrid-columnHeaders": {
                                borderBottom: "2px solid #e0e0e0",
                                backgroundColor: "#f5f8ff",
                            },

                            "& .MuiDataGrid-columnHeader": {
                                padding: "0 16px",
                                fontWeight: 700,
                                fontSize: "16px",
                                color: "#1e66ab",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                            },

                            "& .MuiDataGrid-columnHeaderTitleContainer": {
                                justifyContent: "flex-start",
                                padding: 0,
                            },

                            "& .MuiDataGrid-columnHeaderTitle": {
                                textAlign: "left",
                                width: "fit-content",
                                letterSpacing: "0.2px",
                            },

                            "& .MuiDataGrid-columnSeparator": {
                                display: "none !important",
                            },

                            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                                outline: "none",
                            },

                            /* ── Cells ── */
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                padding: "0 16px",
                                whiteSpace: "normal",
                                color: "#1e66ab",
                                fontSize: "14px",
                                borderBottom: "1px solid #f0f0f0",
                            },

                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },

                            /* ── Checkbox ── */
                            "& .MuiDataGrid-columnHeaderCheckbox": {
                                padding: "0 !important",
                                width: "48px !important",
                                minWidth: "48px !important",
                                maxWidth: "48px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            },

                            "& .MuiDataGrid-cellCheckbox": {
                                padding: "0 !important",
                                width: "48px !important",
                                minWidth: "48px !important",
                                maxWidth: "48px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            },

                            /* ── Rows ── */
                            "& .MuiDataGrid-row": {
                                borderLeft: "4px solid transparent",
                                backgroundColor: "white",
                                boxSizing: "border-box",
                                cursor: "pointer",
                                transition: "background-color 0.15s ease",
                            },

                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f0f4ff",
                                borderLeft: "4px solid #1e66ab33",
                            },

                            /* ── Selected row ── */
                            "& .MuiDataGrid-row.Mui-selected": {
                                backgroundColor: "rgba(150, 193, 33, 0.10) !important",
                                borderLeft: "4px solid #96c121",
                            },

                            "& .MuiDataGrid-row.Mui-selected:hover": {
                                backgroundColor: "rgba(150, 193, 33, 0.18) !important",
                            },

                            /* ── Footer ── */
                            "& .MuiDataGrid-footerContainer": {
                                color: "#1e66ab",
                                borderTop: "2px solid #e0e0e0",
                                backgroundColor: "#f5f8ff",
                                minHeight: "52px",
                            },

                        }}
                        pageSizeOptions={[5, 10, 15]}
                        checkboxSelection
                        keepNonExistentRowsSelected
                        // disableRowSelectionOnClick
                        onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
                    />
                </Box>

            </DialogContent>

            <Divider></Divider>
            <DialogActions sx={{
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
            }}>
                {/* Cancel */}
                <CustomButton
                    capsule
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        width: 'auto',
                        minWidth: 'fit-content',
                        flex: 'none',
                    }}
                >
                    Cancel
                </CustomButton>

                {/* Add Fee Satge */}
                <CustomButton
                    capsule
                    sx={{
                        textTransform: 'none',
                        width: 'auto',
                        minWidth: 'fit-content',
                        flex: 'none',
                    }}
                    onClick={handleSubmit}
                >

                    Add Fee stage

                </CustomButton>

            </DialogActions>
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </Dialog>

    );
}

export default ProposalAddFeeStageModal;